import { PageType } from "@tvg/product-listing-url";
import { CLEARANCE } from "client/utils/consts";
import type { SEOMetadataHandler, SEOMetadataParams } from "server/utils/seo/types";
import { Brand } from "types/common";
import type { BrandFacet, Category, Facet, FacetList, SearchAPIData, SearchAPIMetadata } from "types/search-api";
import { BrandNameMap } from "./consts";

function capitaliseWord(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function formatPriceRanges(priceRanges: string[], { separator, brand }: { separator: string; brand: Brand }): string {
  const currencySymbol = brand === Brand.VeryIE ? "€" : "£";
  return priceRanges
    .map((range) => {
      const parts = range.split("-");
      if (parts[0] === "-inf") {
        return `Up to ${currencySymbol}${parts[1]}`;
      }
      if (parts[1] === "inf") {
        return parts[0] === "" ? `Up to ${currencySymbol}${parts[2]}` : `${currencySymbol}${parts[0]} and more`;
      }
      return `${currencySymbol}${parts[0]}-${currencySymbol}${parts[1]}`;
    })
    .join(separator);
}

export function getCombinedFilters(
  filters: Record<string, string[] | undefined>,
  facetList: FacetList[],
  { separator = " | ", brand }: { separator?: string; brand: Brand },
) {
  if (!filters) {
    return [];
  }

  const combinedFilters: string[] = [];

  const brandsMap: Record<string, string> = {};
  const filterKeys = Object.keys(filters);

  if (filterKeys.some((key) => key === "brand")) {
    const brandFacet = facetList?.find((facet) => facet?.id === "brand");

    if (brandFacet?.values?.length) {
      brandFacet.values.forEach((brand: Facet | BrandFacet) => {
        if (brand?.items?.length) {
          (brand as BrandFacet).items.forEach((brandItem) => {
            if (brandItem.id && brandItem.name) {
              brandsMap[brandItem.id] = brandItem.name;
            }
          });
        }
      });
    }
  }

  Object.keys(filters).forEach((filterKey: string) => {
    if (filterKey === "price") {
      const combinedPriceRanges = filters?.price && formatPriceRanges(filters.price, { separator, brand });
      if (combinedPriceRanges) combinedFilters.push(combinedPriceRanges.replace(".head", ""));
    } else if (filterKey !== "group_id") {
      filters[filterKey]?.forEach((val) => {
        const value = val.replace(".head", "");

        if (filterKey === "customerRating") {
          combinedFilters.push(`Customer Rating ${value}`);
        } else if (filterKey === "navigationColour") {
          const capitalisedValue = capitaliseWord(value);
          combinedFilters.push(capitalisedValue);
        } else if (filterKey === "brand") {
          const brandName = brandsMap[value];

          if (brandName) {
            combinedFilters.push(brandName);
          } else {
            combinedFilters.push(value);
          }
        } else {
          const selectedFacet = facetList?.find((facet) => facet?.id === filterKey);

          if (selectedFacet?.values?.length) {
            let selectedFacetValue = selectedFacet.values.find(
              (facetValue) => (facetValue as Facet)?.id === value,
            ) as Facet;
            if (!selectedFacetValue) {
              selectedFacetValue = selectedFacet.values.find(
                (facetValue) => (facetValue as Facet)?.id.replace(/"(.*?)"/g, "$1") === value,
              ) as Facet;
            }
            if (selectedFacetValue?.name) {
              combinedFilters.push(selectedFacetValue.name);
            } else {
              combinedFilters.push(value);
            }
          } else {
            combinedFilters.push(value);
          }
        }
      });
    }
  });

  return combinedFilters;
}

function getParentCategory(meta: SearchAPIMetadata) {
  return meta.parent_categories.length > 1
    ? meta.parent_categories
        .slice(1)
        .reverse()
        .map((category: Category) => category.display_name)
        .join(" | ")
    : "";
}

export function constructPromoPageTitle(json: SearchAPIData, { brand }: { brand: Brand }): string {
  if (!json) return "Promotion .......";
  const { meta, facet_list } = json;
  const { collection, selected_category } = meta;

  let categoryPath = selected_category?.display_name || "";
  const parentCategoryPath = getParentCategory(meta);
  const collectionName = collection?.display_name;

  if (categoryPath.toLocaleLowerCase() === "all") {
    categoryPath = "";
  }

  const filtersExist = meta?.request?.filters && Object.keys(meta.request.filters).length > 0;

  let titleParts: string[] = [];
  if (collectionName) {
    titleParts.push(collectionName);
  }

  if (categoryPath) {
    titleParts.push(categoryPath);
  }

  if (parentCategoryPath) {
    titleParts.push(parentCategoryPath);
  }
  if (filtersExist && meta.request?.filters) {
    const combinedFilters = getCombinedFilters(meta.request.filters, facet_list, { brand });
    titleParts = titleParts.concat(combinedFilters);
  }

  let title = titleParts.join(" | ");
  if (collectionName) {
    title = `${collectionName} | ${titleParts.slice(1).join(" | ")}`;
  }

  if (!title.endsWith(" | ")) {
    title += " | ";
  }

  return title.replace(".head", "");
}

function isBrand(value: Facet | BrandFacet): value is BrandFacet {
  return "group" in value && !("id" in value);
}

function isClearanceUf(unfilteredFacet: string, facetValue: string | undefined): boolean {
  return unfilteredFacet === "priceType" && facetValue === "clearance";
}

const listShortValuesFacets = [
  "screenSize",
  "customerRating",
  "navigationSizeLadieswearBras",
  "navigationSizeMenswearStandard",
  "navigationSizeLadieswearClothing",
  "navigationSizeLadieswearFootwear",
  "navigationSizeMenswearWaist",
  "navigationSizeMenswearCollar",
  "navigationSizeMenswearChest",
  "navigationSizeMenswearFootwear",
  "navigationSizeChildrenswearClothing",
  "navigationSizeChildrenswearFootwear",
  "dryLoad",
  "dvdCertificate",
  "numberOfNights",
  "numberOfPeople",
  "numberOfSlices",
  "threadCount",
  "togRating",
  "washLoad",
  "numberOfTreatments",
  "discount",
  "microwaveCapacity",
];

function makeItReadeble({ id, name }: FacetList, value: string): string {
  let fullName = value;
  if (listShortValuesFacets.includes(id)) {
    fullName = `${name} ${value}`;
  }
  return fullName;
}

const getFacetValueName = (selectedFacet: FacetList, unfilteredFacetValueId: string | undefined): string => {
  let facetValueName = "";

  if (selectedFacet?.values?.length) {
    selectedFacet.values.forEach((value: Facet | BrandFacet) => {
      const item: Facet | undefined = isBrand(value)
        ? value?.items?.find((brand) => brand.id === unfilteredFacetValueId)
        : value;

      if (item?.id === unfilteredFacetValueId) {
        facetValueName = item?.name || item?.id || "";
      }
    });
  }

  return facetValueName;
};

export const getUnfilteredFacetName = (
  meta: SearchAPIMetadata,
  facetList: FacetList[] | [],
  unfilteredFacet?: string,
): string => {
  const unfilteredFacetValueId = meta?.request?.browse_filter_value;
  let facetName = "";
  if (unfilteredFacet) {
    const selectedFacet = facetList?.find((facet) => facet?.id === unfilteredFacet);
    if (selectedFacet) {
      const valueName = getFacetValueName(selectedFacet, unfilteredFacetValueId);
      facetName = makeItReadeble(selectedFacet, valueName);
    } else if (isClearanceUf(unfilteredFacet, unfilteredFacetValueId)) {
      facetName = CLEARANCE;
    }
  }

  return facetName;
};

const getCategory = (meta: SearchAPIMetadata, facetValueName?: string): string => {
  const displayName = meta?.selected_category?.display_name;
  if (!displayName) {
    return "";
  }

  if (facetValueName) {
    return displayName.toLocaleLowerCase() === "all" ? facetValueName : `${displayName} | ${facetValueName}`;
  }

  return displayName;
};

export const constructPageTitle = (
  response: SearchAPIData,
  { unfilteredFacet, brand }: { unfilteredFacet?: string; brand: Brand },
): string => {
  if (!response) return "Womens, Mens and Kids Fashion, Furniture, Electricals &amp; More!";

  const { meta, facet_list } = response;
  const selectedFacetName = getUnfilteredFacetName(meta, facet_list, unfilteredFacet);

  const categoryPath = getCategory(meta, selectedFacetName);
  const parentCategoryPath = getParentCategory(meta);

  // Handle missing filters case
  if (!meta.request?.filters || !Object.keys(meta.request.filters).length) {
    return parentCategoryPath !== "" ? `${categoryPath} | ${parentCategoryPath} | ` : `${categoryPath} | `;
  }

  const { filters } = meta.request;
  const combinedFilters = filters && getCombinedFilters(filters, facet_list, { brand });

  // Build title parts
  const filterPath = combinedFilters.join(" | ");
  const parentCategoryTitle = parentCategoryPath.length > 0 ? `${parentCategoryPath} | ` : "";
  const title = `${categoryPath && `${categoryPath} | `}${filterPath && `${filterPath} | `}${parentCategoryTitle}`;

  return title.replace(".head", "");
};

export const generatePageTitle = ({
  fallback,
  brand,
  plpurl,
  pageType,
  overrides,
}: Pick<SEOMetadataParams, "fallback" | "brand" | "plpurl" | "pageType" | "overrides">) => {
  const pageTitle =
    overrides?.title ||
    (pageType === PageType.Browse
      ? constructPageTitle(fallback, { unfilteredFacet: plpurl.unfilteredFacet, brand })
      : constructPromoPageTitle(fallback, { brand }));
  const titleCIOWithBrand = `${pageTitle && `${pageTitle}`}${BrandNameMap[brand]}`;
  const title = overrides?.title || titleCIOWithBrand;
  const pageNumber = plpurl?.pageNumber || false;

  return `${title && `${title}`}${Boolean(pageNumber) && Number(pageNumber) > 1 ? ` Page ${pageNumber}` : ""}`;
};

export const getSelectedCategory = (meta: SearchAPIMetadata): string => {
  const selectedCategory = meta.selected_category;

  if (!selectedCategory || selectedCategory.group_id === "all") {
    return "";
  }

  return selectedCategory.display_name || "";
};

export const constructSimplePageTitle = ({
  type,
  term,
  response,
  brand,
}: {
  type: PageType;
  brand: Brand;
  term?: string;
  response?: SearchAPIData | null;
}): string => {
  const searchTerm = response?.meta?.request?.term || term;
  if (type === PageType.Search) return !searchTerm ? "" : `Showing results for "${searchTerm}"`;
  if (!response) return "";

  const { meta, facet_list } = response;

  const filters = meta.request?.filters;
  const collectionName = meta.collection?.display_name || "";
  const selectedCategory = getSelectedCategory(meta);
  const selectedCategoryPath = selectedCategory ? `, ${selectedCategory}` : "";

  if (!filters || !Object.keys(filters).length) {
    const title = `${collectionName}${selectedCategoryPath}`;

    return title.replace(/^,/, "").trim();
  }

  const combinedFilters = getCombinedFilters(filters, facet_list, { separator: ", ", brand });
  const filterPath = combinedFilters.length ? `, ${combinedFilters.join(", ")}` : "";

  const title = `${collectionName}${selectedCategoryPath}${filterPath}`;

  return title.replace(/^,/, "").trim();
};

export const getPageTitle: SEOMetadataHandler = ({ title }) => `<title>${title}</title>`;
